// AI insight + recommendation panels (prototype aiInsight / aiRecs).

import React from 'react';
import { cn } from '@/lib/utils';
import { analyseDrivers } from './engine/insights';
import type { InsightOpts } from './engine/insights';
import type { Worker } from './engine/data';

export const AiInsight: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (children === null || children === undefined || children === false || children === '') return null;
  return (
    <div className='flex items-start gap-3 rounded-xl border border-info/25 bg-gradient-to-br from-info/10 to-violet/10 px-4 py-3.5'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-info/15 text-sm text-info'>✦</div>
      <div className='min-w-0'>
        <div className='mb-1 text-[10px] font-bold uppercase tracking-wider text-info'>AI Insight</div>
        <div className='text-[13px] leading-relaxed text-foreground'>{children}</div>
      </div>
    </div>
  );
};

// Rank 1 = red, 2 = amber, 3+ = blue (prototype rec-priority-1/2/3).
const PRIORITY = [
  { border: 'border-l-destructive', badge: 'bg-destructive/10 text-destructive' },
  { border: 'border-l-warning', badge: 'bg-warning/10 text-warning' },
  { border: 'border-l-info', badge: 'bg-info/10 text-info' },
] as const;

export const AiRecs: React.FC<{ workers: Worker[]; opts?: InsightOpts; scopeLabel?: string }> = ({
  workers,
  opts,
  scopeLabel,
}) => {
  const steps = React.useMemo(() => analyseDrivers(workers, opts), [workers, opts]);
  if (!steps.length) return null;
  const scopeName = scopeLabel || opts?.scopeName || 'this group';
  return (
    <div className='overflow-hidden rounded-xl border border-warning/25 bg-card'>
      <div className='flex items-center gap-2 border-b border-warning/20 bg-warning/10 px-4 py-2.5'>
        <div className='text-xs text-warning'>▶</div>
        <div className='text-[13px] font-semibold'>Recommended next steps</div>
        <div className='ml-auto text-xs text-muted-foreground'>{scopeName}</div>
      </div>
      <div className='divide-y divide-border'>
        {steps.map((s, i) => {
          const p = PRIORITY[Math.min(i, PRIORITY.length - 1)];
          return (
            <div key={s.action} className={cn('flex items-start gap-3 border-l-4 px-4 py-3', p.border)}>
              <div className={cn('flex w-16 shrink-0 flex-col items-center gap-0.5 rounded-lg px-1.5 py-1', p.badge)}>
                <div className='text-base leading-none'>{s.icon}</div>
                <div className='text-[9px] font-bold uppercase tracking-wide'>{s.label}</div>
              </div>
              <div className='min-w-0'>
                <div className='text-[13px] font-semibold'>{s.action}</div>
                <div className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>{s.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
